import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { Bug } from '../data/projectTypes'
import { USER_SETTABLE_STATUSES, STATUS_LABELS, STATUS_TOOLTIPS } from '../../netlify/functions/lib/bug-statuses'
import { useChangeBugSeverityMutation, useChangeBugStatusMutation } from '../services/projectDataApi'
import { SEVERITIES, SEVERITY_TOOLTIPS } from './bugSeverity'
import StatusBadge from './StatusBadge'
import './BugMetadataSelectors.css'

function BugMetadataSelector({ field, value, onChange }: {
  field: 'status' | 'severity'
  value: string
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isStatus = field === 'status'
  const label = isStatus ? 'Status' : 'Severity'
  const options = isStatus ? USER_SETTABLE_STATUSES : SEVERITIES
  const title = (isStatus ? STATUS_TOOLTIPS : SEVERITY_TOOLTIPS)[value] ?? value
  const optionPrefix = isStatus ? 'status' : 'severity-rail'

  useEffect(() => {
    if (!open) return
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  function close() {
    setOpen(false)
    triggerRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const index = items.indexOf(e.target as HTMLElement)
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[(index + 1) % items.length]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[(index - 1 + items.length) % items.length]?.focus()
        break
      case 'Escape':
        e.preventDefault()
        close()
        break
    }
  }

  function renderValue(option: string) {
    return isStatus ? STATUS_LABELS[option] ?? option : <StatusBadge status={option} variant="severity" />
  }

  return (
    <div ref={rootRef} className="bug-status-selector bug-status-selector--block" data-testid={`${field}-select`} data-value={value}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen(o => !o)}
        className="bug-status-selector__btn"
        title={title}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${label}: ${isStatus ? STATUS_LABELS[value] ?? value : title}`}
        data-testid={`${field}-dropdown-btn`}
        data-analytics-event={`bug-${optionPrefix}-toggle`}
      >
        <span className="bug-status-selector__btn-label">{label}</span>
        <span className="bug-status-selector__btn-value">{renderValue(value)}</span>
        <ChevronDown size={14} className="bug-status-selector__caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="bug-status-selector__dropdown" role="menu" aria-label={`${label} options`} onKeyDown={handleKeyDown}>
          {options.map(option => (
            <button
              type="button"
              key={option}
              onClick={() => { onChange(option); close() }}
              className="bug-status-selector__dropdown-item"
              role="menuitem"
              tabIndex={-1}
              data-testid={`set-${optionPrefix}-${option}`}
              data-analytics-event={`bug-${optionPrefix}-${option}`}
            >
              {renderValue(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function BugMetadataSelectors({ bug }: { bug: Bug }) {
  const [changeStatus] = useChangeBugStatusMutation()
  const [changeSeverity] = useChangeBugSeverityMutation()
  return (
    <>
      <BugMetadataSelector field="status" value={bug.status} onChange={status => { void changeStatus({ id: bug.id, status }) }} />
      <BugMetadataSelector field="severity" value={bug.severity} onChange={severity => { void changeSeverity({ id: bug.id, severity }) }} />
    </>
  )
}
